import React from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'

import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {getAuthenticationFlow} from '@vio/core/src/auth/modules/selectors'
import {AuthenticationFlows} from '@vio/core/src/auth/types/Auth'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'

import {useIsPaymentPageSignIn} from '../../../../../authentication/SUSI/overlay/hooks/useIsPaymentPageSignIn'
import {useIsDealFreezeOnboardingVariant} from '../../../../../dealFreeze/hooks/useIsDealFreezeOnboardingVariant'

const BottomSheetTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s100};
  text-align: center;
`

const titles = defineMessages({
  default: {
    id: 'magicLinkModal.titleMemberDeals',
    defaultMessage: 'Unlock member deals for FREE!'
  },
  [AuthenticationFlows.DealFreeze]: {
    id: 'dealFreeze.susiTitle',
    defaultMessage: 'Sign in to freeze this deal'
  },
  vioDeal: {
    id: 'vioDeals.unlock',
    defaultMessage: 'Unlock the best value'
  },
  bookLikeAnInsider: {
    id: 'onlyOnVio.title',
    defaultMessage: 'Book like an insider'
  },
  keepStaysInOnePlace: {
    id: 'keepStaysInOnePlace.title',
    defaultMessage: 'Keep all your stays in one place'
  },
  [AuthenticationFlows.PostOfferCheck]: {
    id: 'lockedRoomOffer.susiTitle',
    defaultMessage: 'Sign in to book this deal'
  },
  [AuthenticationFlows.PreCheckout]: {
    id: 'SUSI.bookSmarter',
    defaultMessage: 'Sign in to book smarter'
  }
})

const descriptions = defineMessages({
  default: {
    id: 'SUSI.joinMembers',
    defaultMessage:
      'Join over 5,000,000 members who are saving on every booking'
  },
  [AuthenticationFlows.DealFreeze]: {
    id: 'dealFreeze.susiDescriptionV2',
    defaultMessage:
      "Guarantee the room and price while you decide. If prices drop, we'll refreeze your deal at the lower rate."
  },
  dealFreezeOnboarding: {
    id: 'dealFreeze.susiDescriptionV3',
    defaultMessage:
      'Freezing a deal guarantees the room and price, so you can decide later.'
  },
  vioDeal: {
    id: 'vioDeals.getFreeAccess',
    defaultMessage:
      'Sign up to {brandName} to get free access to insider hotel deals and save up to 45% on your stays.'
  },
  bookLikeAnInsider: {
    id: 'bookLikeAnInsider.description',
    defaultMessage:
      "Sign in to see insider deals, freeze today's prices while you decide, and keep all your stays in one place."
  },
  keepStaysInOnePlace: {
    id: 'keepStaysInOnePlace.description',
    defaultMessage:
      'Sign in to find, change, or cancel any booking. Without digging through your inbox.'
  },
  [AuthenticationFlows.PostOfferCheck]: {
    id: 'lockedRoomOffer.susiDescription',
    defaultMessage:
      'Book this deal and discover thousands more exclusive market-beating discounts.'
  },
  [AuthenticationFlows.PreCheckout]: {
    id: 'SUSI.saveTime',
    defaultMessage:
      'Save time when booking, get hotel price alerts, and manage all your bookings in one places.'
  }
})

type LoginBanner = {
  isLoginBannerEnabled: boolean
  isPaymentPageSignIn: boolean
  isDealFreezeOnboarding: boolean
}

const flowsWithOwnMessages: (AuthenticationFlows | null)[] = [
  AuthenticationFlows.DealFreeze,
  AuthenticationFlows.PostOfferCheck,
  AuthenticationFlows.PreCheckout
]

const getTitleMessage = (
  flow: AuthenticationFlows | null,
  {isLoginBannerEnabled, isPaymentPageSignIn}: LoginBanner
) => {
  switch (flow) {
    case AuthenticationFlows.DealFreeze:
    case AuthenticationFlows.PostOfferCheck:
    case AuthenticationFlows.PreCheckout:
      return titles[flow]
  }

  if (isLoginBannerEnabled) {
    return isPaymentPageSignIn
      ? titles.keepStaysInOnePlace
      : titles.bookLikeAnInsider
  }

  return titles.vioDeal
}

const getDescriptionMessage = (
  flow: AuthenticationFlows | null,
  {
    isLoginBannerEnabled,
    isPaymentPageSignIn,
    isDealFreezeOnboarding
  }: LoginBanner
) => {
  switch (flow) {
    case AuthenticationFlows.DealFreeze:
      return isDealFreezeOnboarding
        ? descriptions.dealFreezeOnboarding
        : descriptions[flow]
    case AuthenticationFlows.PostOfferCheck:
    case AuthenticationFlows.PreCheckout:
      return descriptions[flow]
  }

  if (isLoginBannerEnabled) {
    return isPaymentPageSignIn
      ? descriptions.keepStaysInOnePlace
      : descriptions.bookLikeAnInsider
  }

  return descriptions.vioDeal
}

export const SignInTitle = () => {
  const {formatMessage} = useIntl()
  const authenticationFlow = useSelector(getAuthenticationFlow)
  const {brandName} = useBrand()
  const toggle = useToggle()

  const isPaymentPageSignIn = useIsPaymentPageSignIn()
  const isDealFreezeOnboarding = useIsDealFreezeOnboardingVariant(
    authenticationFlow === AuthenticationFlows.DealFreeze
  )

  // Only read the toggle for flows that can see the change, so the other flows
  // are not allocated to the experiment.
  const loginBanner = {
    isLoginBannerEnabled:
      !flowsWithOwnMessages.includes(authenticationFlow) &&
      toggle('1cmqc2sk-login-change-on-bovio-funnel', false, true),
    isPaymentPageSignIn,
    isDealFreezeOnboarding
  }

  const title = formatMessage(getTitleMessage(authenticationFlow, loginBanner))
  const description = formatMessage(
    getDescriptionMessage(authenticationFlow, loginBanner),
    {brandName}
  )

  return (
    <BottomSheetTitle>
      <Text variant="titleXL" dataId="vioDeals.unlock">
        {title}
      </Text>
      <div>
        <Text>{description}</Text>
      </div>
    </BottomSheetTitle>
  )
}
